import type { MeDevice } from '@dyspatch/core';
import { Link } from '@tanstack/react-router';
import { Pause, Play } from 'lucide-react';
import { useSession } from '@/components/auth/session';
import { RowSkeleton } from '@/components/common/bits';
import { ClaudeMark, DeviceMark } from '@/components/common/marks';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import type { AccountData } from '@/features/account/data';
import { DeviceUpdateNotice } from '@/features/fleet/DeviceVersion';
import { HeadroomBar } from '@/features/fleet/HeadroomBar';
import { useHubAction } from '@/hooks/use-hub-action';
import { api } from '@/lib/api';
import { duration, minutesSince, percent } from '@/lib/format';

/**
 * Pause a device, where the hub will allow it.
 *
 * `/api/workers/:id/dnd` is in `adminOnlyRoutes`, so a member pressing this
 * would get a 403 and a toast reading "forbidden". A control that is drawn for
 * everybody and works for one person is worse than no control, so the member
 * gets the sentence instead: the same fact, and the thing they can actually do
 * about it, which is quit the agent on their own laptop.
 */
function PauseDevice({ device }: { device: MeDevice }) {
  const { session } = useSession();
  const pause = useHubAction((dnd: boolean) => api.setDnd(device.id, dnd), {
    success: (dnd) => (dnd ? `${device.name} is paused.` : `${device.name} is taking work again.`),
  });

  if (!session.isAdmin) {
    return (
      <span className="text-fg-3 shrink-0 text-xs">
        {device.dnd ? 'Paused by an admin' : 'Quit the agent to stop taking work'}
      </span>
    );
  }

  return (
    <Button
      size="sm"
      variant="outline"
      className="shrink-0"
      disabled={pause.busy}
      onClick={() => pause.run(!device.dnd)}
      title={
        device.dnd
          ? 'Let this device take work again.'
          : 'Stop offering this device new work. Anything already running finishes.'
      }
    >
      {device.dnd ? <Play aria-hidden /> : <Pause aria-hidden />}
      {device.dnd ? 'Resume' : 'Pause'}
    </Button>
  );
}

/** One of your machines, and everything configured about it. */
function DeviceCard({ device }: { device: MeDevice }) {
  const live = device.presence !== 'offline' && !device.dnd;
  const tone = device.dnd
    ? 'var(--dy-warn)'
    : device.presence === 'offline'
      ? 'var(--dy-fg-3)'
      : 'var(--dy-ok)';

  return (
    <li className="min-w-0 px-gutter py-4">
      <div className="flex min-w-0 items-center gap-3">
        <DeviceMark hint={device.hostname || device.name} tone={tone} />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="truncate text-md font-medium">{device.name}</span>
            <Badge variant={live ? 'success' : device.dnd ? 'warning' : 'secondary'}>
              {device.dnd ? 'paused' : device.presence}
            </Badge>
            {device.versionUpdate?.status === 'blocked' ? (
              <Badge variant="destructive" title={device.versionUpdate.detail}>
                Not taking work
              </Badge>
            ) : null}
          </div>
          <p className="text-fg-3 mt-0.5 truncate text-xs">
            {device.hostname || 'unknown host'} · heartbeat{' '}
            {device.lastHeartbeatAt
              ? `${duration(minutesSince(device.lastHeartbeatAt))} ago`
              : 'never'}
            {device.workerVersion ? ` · worker ${device.workerVersion}` : ''}
            {/* The projects this device is mapped to used to be a third of the
                card: an eyebrow, a row of chips and a column wide enough to hold
                them, to say two names that rarely change. They are one fact about
                the machine, so they sit on the line with the machine's other
                facts, and only the case that changes what the device does (mapped
                to nothing, therefore offered nothing) is spelled out. */}
            {device.repos.length
              ? ` · ${device.repos.join(', ')}`
              : ' · no projects mapped, so nothing is offered to it'}
          </p>
        </div>
        <PauseDevice device={device} />
      </div>

      {device.versionUpdate ? (
        <DeviceUpdateNotice className="mt-2.5" update={device.versionUpdate} name={device.name} />
      ) : null}

      <HeadroomBar
        headroom={device.headroom}
        source={device.headroomSource}
        window={device.headroomWindow}
        measuredAt={device.headroomMeasuredAt}
      />

      <div className="mt-3 grid gap-x-6 gap-y-2.5 sm:grid-cols-[8rem_8rem]">
        <div>
          <div className="dy-eyebrow">Running</div>
          <div className="tnum mt-0.5 flex items-center gap-1.5 text-base">
            <ClaudeMark className="size-3.5 opacity-70" />
            {device.activeTaskCount} of {device.maxConcurrency}
          </div>
        </div>
        <div>
          <div className="dy-eyebrow">Concurrency</div>
          <div className="tnum mt-0.5 text-base">{device.maxConcurrency} at once</div>
        </div>
      </div>
    </li>
  );
}

/**
 * Your agents, with the configuration that belongs to them: the account window,
 * the concurrency, the projects they are mapped to, and pausing one. That last is
 * the only device write the hub exposes and it is admin-gated on the server, so
 * it is drawn for an admin and explained to everybody else.
 *
 * While the first read is in flight the region draws skeleton rows rather than
 * the empty state: an empty list then means nothing has arrived yet, not that
 * nothing is enrolled, and claiming the latter on every load and taking it back
 * a moment later is worse than a wait.
 */
export function AgentsSection({ data }: { data: AccountData }) {
  const { devices, account, loading } = data;

  return (
    <Section>
      <SectionHead
        title="Your agents"
        meta={
          account
            ? `${percent(account.headroom)} of the account window left · ${account.load} of ${account.maxConcurrentTasks} concurrent tasks in use`
            : devices.length
              ? 'none of them is online, so there is no window to report'
              : undefined
        }
        actions={
          <Button size="sm" variant="outline" asChild>
            <Link to="/agents">What an agent is →</Link>
          </Button>
        }
      />
      <Surface>
        {devices.length ? (
          <ul className="divide-line divide-y">
            {devices.map((device) => (
              <DeviceCard key={device.id} device={device} />
            ))}
          </ul>
        ) : loading ? (
          <RowSkeleton />
        ) : (
          <div className="px-gutter py-10 text-center">
            <ClaudeMark className="text-fg-3 mx-auto mb-3 size-6" />
            <p className="text-foreground text-lg font-medium">
              No device is enrolled under your name
            </p>
            <p className="text-fg-3 mx-auto mt-1.5 max-w-md text-sm">
              An agent is Claude Code running on your own laptop, under your own subscription.
              Connecting one is your own business, not an admin's: Agents mints your code and waits
              on the page until the device checks in.
            </p>
            <Button size="sm" className="mt-3" asChild>
              <Link to="/agents">Connect a laptop</Link>
            </Button>
          </div>
        )}
      </Surface>
    </Section>
  );
}
